/**
 * Visa Checker Service
 * 
 * Provides visa requirement checking and checklist management for trips.
 * Integrates with backend visa service and document vault.
 */

import { httpsCallableWithTimeout, functions } from '@/lib/functions';
import type { Trip } from './trips';
import {
  getTripDestinationCountryLabel,
  resolveTripDestinationCountryIso2,
} from '@/utils/tripDestinationCountry';
import { fromIso2CountryCode, toIso2CountryCode } from '@/utils/countryIso2';
import type { Document } from './documentVault';

export type VisaStatus = 'visa_free' | 'visa_required' | 'visa_on_arrival' | 'e_visa' | 'unknown';

export type VisaRequirement = {
  status: VisaStatus;
  visaRequired: boolean;
  stayDuration?: number; // Days allowed
  notes?: string;
  eVisaLink?: string;
  embassyLink?: string;
  lastUpdated?: string;
  destination: string;
  passportCountry: string;
};

export type VisaChecklistItem = {
  id: string;
  requirement: string;
  completed: boolean;
  documentId?: string; // Link to document in vault
  dueDate?: Date;
  notes?: string;
  /** When set, show linkLabel as tappable link (no naked URL in notes) */
  linkUrl?: string;
  linkLabel?: string;
};

type CheckVisaRequirementsRequest = {
  passportCountry: string;
  destinationCountry: string;
};

type CheckVisaRequirementsResponse = {
  status: VisaStatus;
  visaRequired: boolean;
  stayDuration?: number;
  notes?: string;
  eVisaLink?: string;
  embassyLink?: string;
  lastUpdated?: string;
};

const checkVisaRequirementsCallableWithTimeout = httpsCallableWithTimeout<
  CheckVisaRequirementsRequest,
  CheckVisaRequirementsResponse
>(functions, 'checkVisaRequirements', 45000);

function normalizeCountry(value: string): string {
  const v = (value || '').trim();
  if (!v) return v;
  const lower = v.toLowerCase();
  if (lower === 'us' || lower === 'usa' || lower === 'united states' || lower === 'united states of america') return 'US';
  if (lower === 'uk' || lower === 'united kingdom' || lower === 'great britain') return 'GB';
  return v.length === 2 ? v.toUpperCase() : v;
}

/**
 * Check visa requirements for a trip
 */
export async function checkVisaRequirements(
  trip: Trip,
  passportCountry: string
): Promise<VisaRequirement | null> {
  try {
    const destinationIso2 = resolveTripDestinationCountryIso2(trip);
    if (!destinationIso2 || !passportCountry) {
      return null;
    }

    const passportIso2 =
      toIso2CountryCode(passportCountry) ||
      (passportCountry.trim().length === 2 ? passportCountry.trim().toUpperCase() : undefined);
    if (!passportIso2) {
      return null;
    }

    const destinationLabel =
      getTripDestinationCountryLabel(trip) || fromIso2CountryCode(destinationIso2) || destinationIso2;

    // Same-country travel is visa-free.
    if (passportIso2 === destinationIso2) {
      return {
        status: 'visa_free',
        visaRequired: false,
        notes: `No visa required for citizens of ${fromIso2CountryCode(passportIso2) || passportIso2} traveling within ${destinationLabel}.`,
        lastUpdated: new Date().toISOString(),
        destination: destinationLabel,
        passportCountry: fromIso2CountryCode(passportIso2) || passportIso2,
      };
    }

    const result = await checkVisaRequirementsCallableWithTimeout({
      passportCountry: passportIso2,
      destinationCountry: destinationIso2,
    });

    return {
      ...result.data,
      destination: destinationLabel,
      passportCountry: fromIso2CountryCode(passportIso2) || passportIso2,
    };
  } catch (error) {
    console.error('[VisaChecker] Error checking visa requirements:', error);
    return null;
  }
}

/**
 * Generate checklist items based on visa requirement
 */
export function generateVisaChecklist(
  requirement: VisaRequirement,
  trip: Trip
): VisaChecklistItem[] {
  const items: VisaChecklistItem[] = [];

  if (requirement.status === 'visa_free') {
    items.push({
      id: 'visa-free-confirm',
      requirement: 'Confirm visa-free entry eligibility',
      completed: false,
      notes: `Citizens of ${requirement.passportCountry} can enter ${requirement.destination} without a visa${requirement.stayDuration ? ` for up to ${requirement.stayDuration} days` : ''}.`,
    });
    return items;
  }

  if (requirement.visaRequired) {
    // Main visa application
    items.push({
      id: 'apply-visa',
      requirement: `Apply for ${requirement.status === 'e_visa' ? 'e-visa' : requirement.status === 'visa_on_arrival' ? 'visa on arrival' : 'visa'}`,
      completed: false,
      dueDate: new Date(trip.startDate.getTime() - 30 * 24 * 60 * 60 * 1000), // 30 days before trip
      notes: requirement.notes || `Visa required for ${requirement.passportCountry} citizens traveling to ${requirement.destination}.`,
    });

    // e-Visa specific
    if (requirement.status === 'e_visa' && requirement.eVisaLink) {
      items.push({
        id: 'apply-evisa',
        requirement: 'Complete e-visa application online',
        completed: false,
        dueDate: new Date(trip.startDate.getTime() - 14 * 24 * 60 * 60 * 1000), // 14 days before
        notes: 'Apply online at ',
        linkUrl: requirement.eVisaLink,
        linkLabel: 'Apply for e-visa',
      });
    }

    // Embassy visit
    if (requirement.status === 'visa_required' && requirement.embassyLink) {
      items.push({
        id: 'visit-embassy',
        requirement: 'Visit embassy or consulate',
        completed: false,
        dueDate: new Date(trip.startDate.getTime() - 45 * 24 * 60 * 60 * 1000), // 45 days before
        notes: 'Find embassy information at ',
        linkUrl: requirement.embassyLink,
        linkLabel: 'Embassy finder',
      });
    }

    // Supporting documents
    items.push({
      id: 'gather-documents',
      requirement: 'Gather required supporting documents',
      completed: false,
      notes: 'Passport, photos, travel itinerary, proof of accommodation, financial statements, etc.',
    });

    // Passport validity
    items.push({
      id: 'check-passport',
      requirement: 'Verify passport validity (6+ months)',
      completed: false,
      notes: 'Most countries require passport to be valid for at least 6 months beyond travel dates.',
    });
  }

  // Visa on arrival
  if (requirement.status === 'visa_on_arrival') {
    items.push({
      id: 'prepare-arrival',
      requirement: 'Prepare documents for visa on arrival',
      completed: false,
      notes: 'Bring passport, photos, proof of accommodation, return ticket, and sufficient funds.',
    });
  }

  return items;
}

/**
 * Check if visa documents are linked in document vault
 */
export function linkVisaDocuments(
  checklist: VisaChecklistItem[],
  documents: Document[]
): VisaChecklistItem[] {
  return checklist.map((item) => {
    // Look for visa documents that match this requirement
    const visaDoc = documents.find(
      (doc) =>
        doc.type === 'visa' &&
        doc.metadata &&
        (doc.metadata as any).destinationCountry === item.requirement
    );

    if (visaDoc) {
      return {
        ...item,
        documentId: visaDoc.id,
        completed: true,
      };
    }

    return item;
  });
}

/**
 * Extract country name from location string
 */
function extractCountry(location: string): string {
  // Handle "City, Country" format
  const parts = location.split(',').map((p) => p.trim());
  if (parts.length > 1) {
    return parts[parts.length - 1];
  }
  return location;
}

/**
 * Get visa status message for display
 */
export function getVisaStatusMessage(requirement: VisaRequirement): string {
  switch (requirement.status) {
    case 'visa_free':
      return `No visa required for ${requirement.passportCountry} citizens traveling to ${requirement.destination}${requirement.stayDuration ? ` (up to ${requirement.stayDuration} days)` : ''}.`;
    case 'e_visa':
      return `E-visa required for ${requirement.passportCountry} citizens traveling to ${requirement.destination}.`;
    case 'visa_on_arrival':
      return `Visa on arrival available for ${requirement.passportCountry} citizens traveling to ${requirement.destination}.`;
    case 'visa_required':
      return `Visa required for ${requirement.passportCountry} citizens traveling to ${requirement.destination}.`;
    default:
      return `Please verify visa requirements for ${requirement.passportCountry} citizens traveling to ${requirement.destination}.`;
  }
}

